import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ArrowLeft, BookPlus, Plus } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { v4 as uuid } from 'uuid';
import apiClient from '../services/apiClient';
import { t } from '../i18n';
import ConfirmDialog from './ConfirmDialog';
import VocabularyBeanstalk, { VocabularySproutScene } from './VocabularyBeanstalk';
import { stageVocabularySession } from '../services/VocabularyLearningSession';
import { unlockListeningAudio } from '../services/ListeningAudioTransport';
import './VocabularyPage.css';

const v = (key, values) => t(`vocabulary.${key}`, undefined, values);
const idOf = note => String(note.noteId || note.id);
const titleOf = note => note.title || note.noteTitle;
const cardCount = note => Number(note.cardCount ?? note.flashcardCount) || 0;

export default function VocabularyPage() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [moving, setMoving] = useState(false);
  const [deleteCandidate, setDeleteCandidate] = useState(null);
  const [notice, setNotice] = useState('');
  const pageRef = useRef(null);
  const trainingDockRef = useRef(null);
  const alive = useRef(false);
  const inFlight = useRef(false);
  const retryOperation = useRef(null);
  const pendingStart = useRef(null);
  const refresh = useCallback(async () => {
    const next = await apiClient.getVocabularyOverview();
    if (!alive.current) return;
    setData(next);
    setLoading(false);
  }, []);
  useEffect(() => {
    alive.current = true;
    refresh().catch(err => { if (alive.current) { retryOperation.current = refresh; setError(err.message || v('failed')); setLoading(false); } });
    return () => { alive.current = false; };
  }, [refresh]);
  const notes = useMemo(() => data?.notes || [], [data?.notes]);
  const hasTrainingDock = Boolean(data && notes.length);
  useEffect(() => {
    const page = pageRef.current;
    const dock = trainingDockRef.current;
    if (!hasTrainingDock || !page || !dock) return undefined;
    const measure = () => {
      const height = Math.ceil(dock.getBoundingClientRect().height);
      if (height > 0) page.style.setProperty('--vocabulary-dock-height', `${height}px`);
    };
    measure();
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(measure);
    observer?.observe(dock);
    window.addEventListener('resize', measure);
    return () => { observer?.disconnect(); window.removeEventListener('resize', measure); page.style.removeProperty('--vocabulary-dock-height'); };
  }, [hasTrainingDock]);
  const canLearn = Boolean(data?.queueSummary?.eligibleCount);
  const perform = useCallback(async task => {
    if (inFlight.current) return;
    inFlight.current = true; setBusy(true); setError(''); retryOperation.current = task;
    try { await task(); if (alive.current) retryOperation.current = null; }
    catch (err) { if (alive.current) setError(err.message || v('networkFailed')); }
    finally { inFlight.current = false; if (alive.current) setBusy(false); }
  }, []);
  const start = noteId => {
    if (inFlight.current) return;
    const selected = noteId && notes.find(note => idOf(note) === noteId);
    if (selected?.isEnabled === false || (selected && !cardCount(selected))) { navigate(`/vocabulary/notes/${noteId}`); return; }
    void unlockListeningAudio();
    const startNoteId = noteId || null;
    if (!pendingStart.current || pendingStart.current.startNoteId !== startNoteId) {
      pendingStart.current = { operationId: uuid(), startNoteId, scoringRuleVersion: 3 };
    }
    const payload = pendingStart.current;
    void perform(async () => {
      const session = await apiClient.startVocabularySession(payload);
      if (!alive.current) return;
      pendingStart.current = null;
      if (session.status === 'active') navigate('/vocabulary/practice', { state: stageVocabularySession(session) });
      else await refresh();
    });
  };
  const enterPractice = () => start(null);
  const toggle = noteId => {
    const note = notes.find(item => idOf(item) === noteId);
    const operationId = uuid();
    void perform(async () => { await apiClient.setNoteEnabled(noteId, note.isEnabled === false, { studyKind: 'vocabulary', operationId }); await refresh(); });
  };
  const changeDirection = (noteId, direction) => {
    if (inFlight.current) return;
    const note = notes.find(item => idOf(item) === noteId);
    if (!note || (note.vocabularyDirection || 'q_to_a') === direction) return;
    const operationId = uuid();
    pendingStart.current = null; setNotice('');
    void perform(async () => {
      await apiClient.setVocabularyNoteDirection(noteId, direction, { operationId });
      await refresh();
      if (alive.current) setNotice(`${titleOf(note)}: ${v('directionSaved')}`);
    });
  };
  const remove = () => {
    const noteId = idOf(deleteCandidate); const operationId = uuid(); setDeleteCandidate(null);
    void perform(async () => { await apiClient.deleteNote(noteId, { studyKind: 'vocabulary', operationId }); await refresh(); });
  };
  const reorder = (from, to) => {
    if (from < 0 || to < 0 || from >= notes.length || to >= notes.length || from === to || inFlight.current) return;
    const ordered = [...notes];
    const [moved] = ordered.splice(from, 1);
    ordered.splice(to, 0, moved);
    const operationId = uuid();
    setNotice('');
    void perform(async () => {
      await apiClient.reorderNotes(ordered.map(idOf), { studyKind: 'vocabulary', operationId });
      await refresh();
      if (alive.current) setNotice(`${titleOf(moved)}: ${to + 1} / ${notes.length}. ${v('orderSaved')}`);
    });
  };
  const availability = data?.availability;
  const total = Number(data?.queueSummary?.totalQueueCards) || 0;
  const hasCards = total > 0;
  const noBooks = notes.length === 0;
  const importBooks = () => navigate('/library?view=all&studyKind=vocabulary');
  const openBook = id => navigate(`/vocabulary/notes/${id}`);
  const learningControls = <div className={`vocabulary-learning-controls${noBooks ? ' vocabulary-import-controls' : ''}`}>
    <button type="button" className="vocabulary-primary" disabled={busy || (!noBooks && !canLearn)} onClick={noBooks ? importBooks : enterPractice}>{v(noBooks ? 'importToStart' : hasCards ? 'startTraining' : 'startAll')}</button>
  </div>;
  const availabilityNotice = <>
    {availability?.status === 'all_complete' && <p className="vocabulary-notice" role="status"><span className="vocabulary-heading-caption">{v('complete')} {v('completeMessage')}</span></p>}
    {availability?.status === 'waiting' && <p className="vocabulary-notice" role="status"><span className="vocabulary-heading-caption">{v('waiting')} {availability.nextEligibleDay && v('waitingUntil', { date: availability.nextEligibleDay })}</span></p>}
    {availability?.status === 'inactive' && <p className="vocabulary-notice" role="status"><span className="vocabulary-heading-caption">{v('inactive')}</span></p>}
  </>;
  const headerContent = <>
    <button type="button" className="btn-back-inline" onClick={() => navigate('/')} aria-label={t('learningNavigation.common.backHome')}><ArrowLeft size={22} aria-hidden="true" /></button>
    <div className="app-page-header-copy"><h1 id="vocabulary-page-title"><span className="vocabulary-heading-caption">{v('pageTitle')}</span></h1></div>
    <div className="vocabulary-library-actions">
      <button type="button" disabled={busy || !data} aria-label={v('create')} title={v('create')} onClick={() => navigate('/vocabulary/new')}><Plus size={20} aria-hidden="true" /></button>
      <button type="button" disabled={busy || !data} aria-label={v('import')} title={v('import')} onClick={importBooks}><BookPlus size={20} aria-hidden="true" /></button>
    </div>
    {!hasCards && (!data || !noBooks) && <p className="vocabulary-page-description"><span className="vocabulary-heading-caption">{v('description')}</span></p>}
    {!hasCards && data && !noBooks && <p className="vocabulary-collection-counts"><span className="vocabulary-heading-caption">{v('eligible', { count: data.queueSummary?.eligibleCount || 0 })} · {v('waitingCards', { count: Math.max(0, (data.queueSummary?.pendingCount || 0) - (data.queueSummary?.eligibleCount || 0)) })}</span></p>}
    {hasCards && availabilityNotice}
  </>;
  const pageHeader = <header className="app-page-header vocabulary-page-heading" data-testid="vocabulary-page-heading">{headerContent}</header>;
  const pageFeedback = <>
    {loading && <div className="vocabulary-books-loading" role="status" aria-label={v('loading')}><span /><span /><span /></div>}
    {error && <div className="vocabulary-error" role="alert"><p>{error}</p><button type="button" onClick={() => void perform(retryOperation.current || refresh)} disabled={busy}>{v('retry')}</button></div>}
  </>;
  const collectionTools = <>
    {moving && <p className="vocabulary-order-help">{v('mapReorderHelp')}</p>}
    {notice && <p className="vocabulary-order-help" role="status">{notice}</p>}
  </>;
  const collectionHeader = <>{pageHeader}{pageFeedback}{collectionTools}</>;
  return <main ref={pageRef} className={`vocabulary-page${hasCards ? ' is-map-view' : data && noBooks ? ' is-empty-view' : ''}${hasTrainingDock ? ' has-training-dock' : ''}`}>
    <div aria-hidden={deleteCandidate ? true : undefined} inert={deleteCandidate ? '' : undefined}>
      {!data && <>{pageHeader}{pageFeedback}</>}
      {data && <>
        <section className="vocabulary-collection" aria-labelledby="vocabulary-page-title">
          {!hasCards && (noBooks ? <>
            {collectionHeader}
            <VocabularySproutScene />
          </> : <div className="vocabulary-training-hero" role="group" aria-label={v('pageTitle')}>
            <VocabularySproutScene />
            <div className="vocabulary-hero-content">
              <div>{collectionHeader}</div>
            </div>
          </div>)}
          {!hasCards && availabilityNotice}
          {(hasCards || !noBooks) && <VocabularyBeanstalk notes={notes} total={total} progress={data.queueProgress} moving={moving} busy={busy} showEmptyIllustration={false}
            collectionHeader={hasCards ? headerContent : null} collectionFeedback={hasCards ? <>{pageFeedback}{collectionTools}</> : null} scrollBottomBoundaryRef={trainingDockRef}
            onOpen={openBook} onEdit={id => navigate(`/vocabulary/notes/${id}/edit`)} onToggleEnabled={toggle} onSetDirection={changeDirection}
            onDelete={id => setDeleteCandidate(notes.find(note => idOf(note) === id))} onReorder={reorder}
            onMoveMode={() => setMoving(current => !current)} />}
          {noBooks && <div className="vocabulary-empty">
            <h3>{v('emptyBooks')}</h3>
            <p>{v('emptyBooksDescription')}</p>
            {!hasCards && learningControls}
          </div>}
        </section>
      </>}
      {hasTrainingDock && <div ref={trainingDockRef} className="vocabulary-training-dock" data-testid="vocabulary-training-dock">{learningControls}</div>}
    </div>
    <ConfirmDialog isOpen={Boolean(deleteCandidate)} title={v('deleteTitle')} message={v('deleteMessage', { title: deleteCandidate && titleOf(deleteCandidate) })} confirmLabel={v('delete')} tone="danger" onConfirm={remove} onCancel={() => setDeleteCandidate(null)} />
  </main>;
}
